import type * as TypeScript from 'typescript';
import {
  managedColumnExpr,
  managedColumnLine,
  managedSqlName,
  type ManagedColumnRequirement,
  type ManagedGroupRequirement,
  type ManagedPhysicalColumnRequirement,
  type NamingConvention,
  type SchemaDialect,
} from './managed-columns.js';

export type TypeScriptApi = Omit<typeof TypeScript, 'default'>;
export type SchemaTableStyle = 'q-feature' | 'q-table' | 'raw-drizzle';
export type StaticDeleteMode = 'hard' | 'soft' | 'default' | 'dynamic';

export interface SchemaTableContext {
  /** Stable within one source file, even when SQL table names repeat. */
  id: string;
  fileName: string;
  tableName: string;
  variableName?: string;
  style: SchemaTableStyle;
  dialect: SchemaDialect;
  namingConvention: NamingConvention;
  hasResourceConfig: boolean;
  deleteMode: StaticDeleteMode;
  /**
   * `crud.batchDelete.mode`, inheriting `deleteMode` when unset — the same
   * resolution the compiler applies in `resolveDeleteModes`. Soft-delete
   * columns are required unless BOTH paths are hard, so a resolver that reads
   * only `deleteMode` under-reports the requirement for
   * `delete: { mode: 'hard' }, batchDelete: { mode: 'soft' }`.
   */
  batchDeleteMode: StaticDeleteMode;
  sourceStart: number;
  sourceEnd: number;
}

export type ManagedRequirementResolver = (
  table: SchemaTableContext,
) => ReadonlyArray<ManagedColumnRequirement>;

export interface SchemaSourceEdit {
  start: number;
  end: number;
  text: string;
  tableId?: string;
  reason: string;
}

export type SchemaSyncDiagnosticCode =
  | 'DIVERGENT_MANAGED_COLUMN'
  | 'DUPLICATE_MANAGED_REPRESENTATION'
  | 'DUPLICATE_TABLE_COLUMN'
  | 'MISSING_VALUE_IMPORT'
  | 'SOURCE_PARSE_ERROR'
  | 'UNSUPPORTED_DYNAMIC_DELETE_MODE'
  | 'REQUIREMENT_RESOLUTION_FAILED'
  | 'INVALID_REQUIREMENT'
  | 'OVERLAPPING_SOURCE_EDITS';

export interface SchemaSyncDiagnostic {
  code: SchemaSyncDiagnosticCode;
  message: string;
  fileName: string;
  tableId?: string;
  tableName?: string;
  start?: number;
  end?: number;
  expected?: string;
  actual?: string;
}

export interface PlanSchemaSourceOptions {
  fileName: string;
  source: string;
  typescript: TypeScriptApi;
  dialect: SchemaDialect;
  namingConvention?: NamingConvention;
  resolveRequirements: ManagedRequirementResolver;
}

export interface SchemaSourcePlan {
  fileName: string;
  source: string;
  tables: ReadonlyArray<SchemaTableContext>;
  edits: ReadonlyArray<SchemaSourceEdit>;
  diagnostics: ReadonlyArray<SchemaSyncDiagnostic>;
  updatedSource: string;
  changed: boolean;
  canApply: boolean;
}

interface DiscoveredTable {
  context: SchemaTableContext;
  columns: TypeScript.ObjectLiteralExpression;
}

interface PlannedEntry {
  source: string;
  order: number;
}

interface ImportRequirement {
  identifiers: Set<string>;
  tableIds: Set<string>;
}

const MANAGED_MARKER =
  '// ── quickback:audit (compiler-managed — edits are validated, not merged) ──';

/**
 * Inspect every table in one source file and calculate formatting-preserving,
 * additive-only source edits. No edit is applied here.
 */
export function planSchemaSource(options: PlanSchemaSourceOptions): SchemaSourcePlan {
  const ts = options.typescript;
  const namingConvention = options.namingConvention ?? 'snake_case';
  const sourceFile = ts.createSourceFile(
    options.fileName,
    options.source,
    ts.ScriptTarget.Latest,
    true,
    options.fileName.endsWith('.tsx') ? ts.ScriptKind.TSX : ts.ScriptKind.TS,
  );
  const diagnostics: SchemaSyncDiagnostic[] = [];
  const parseDiagnostics = (
    sourceFile as TypeScript.SourceFile & {
      parseDiagnostics?: ReadonlyArray<TypeScript.Diagnostic>;
    }
  ).parseDiagnostics;
  if (parseDiagnostics && parseDiagnostics.length > 0) {
    for (const diagnostic of parseDiagnostics) {
      diagnostics.push({
        code: 'SOURCE_PARSE_ERROR',
        fileName: options.fileName,
        start: diagnostic.start,
        end:
          diagnostic.start !== undefined && diagnostic.length !== undefined
            ? diagnostic.start + diagnostic.length
            : undefined,
        message:
          `TypeScript could not parse this schema source: ` +
          ts.flattenDiagnosticMessageText(diagnostic.messageText, '\n'),
      });
    }
    return {
      fileName: options.fileName,
      source: options.source,
      tables: [],
      edits: [],
      diagnostics,
      updatedSource: options.source,
      changed: false,
      canApply: false,
    };
  }
  const discovered = discoverTables(ts, sourceFile, options.dialect, namingConvention);
  const edits: SchemaSourceEdit[] = [];
  const imports = new Map<string, ImportRequirement>();

  for (const table of discovered) {
    let requirements: ReadonlyArray<ManagedColumnRequirement>;
    try {
      requirements = options.resolveRequirements(table.context);
    } catch (error) {
      diagnostics.push({
        code: 'REQUIREMENT_RESOLUTION_FAILED',
        fileName: options.fileName,
        tableId: table.context.id,
        tableName: table.context.tableName,
        start: table.context.sourceStart,
        end: table.context.sourceEnd,
        message:
          `Could not determine managed-column requirements for table ` +
          `"${table.context.tableName}": ${error instanceof Error ? error.message : String(error)}`,
      });
      continue;
    }
    if (!Array.isArray(requirements)) {
      diagnostics.push({
        code: 'INVALID_REQUIREMENT',
        fileName: options.fileName,
        tableId: table.context.id,
        tableName: table.context.tableName,
        message:
          `The managed-column resolver for table "${table.context.tableName}" did not return an array.`,
      });
      continue;
    }

    const entries: PlannedEntry[] = [];
    const seenKinds = new Set<string>();
    let needsManagedMarker = false;

    for (const requirement of requirements) {
      const key =
        requirement.kind === 'scope'
          ? `${requirement.kind}:${requirement.jsName}`
          : requirement.kind;
      if (seenKinds.has(key)) {
        diagnostics.push({
          code: 'INVALID_REQUIREMENT',
          fileName: options.fileName,
          tableId: table.context.id,
          tableName: table.context.tableName,
          message: `Requirement "${key}" was supplied more than once for table "${table.context.tableName}".`,
        });
        continue;
      }
      seenKinds.add(key);

      if (isManagedGroupRequirement(requirement)) {
        const result = planManagedGroup(
          ts,
          sourceFile,
          table,
          requirement,
          options.fileName,
          diagnostics,
        );
        entries.push(...result.entries);
        needsManagedMarker ||= result.needsMarker;
        for (const identifier of result.requiredImports) {
          addImportRequirement(
            imports,
            drizzleCoreModule(table.context.dialect),
            identifier,
            table.context.id,
          );
        }
        if (result.needsQImport) {
          addImportRequirement(imports, '@quickback/compiler', 'q', table.context.id);
        }
        continue;
      }

      const result = planPhysicalColumn(
        ts,
        sourceFile,
        table,
        requirement,
        options.fileName,
        diagnostics,
      );
      entries.push(...result.entries);
      for (const identifier of result.requiredImports) {
        addImportRequirement(
          imports,
          drizzleCoreModule(table.context.dialect),
          identifier,
          table.context.id,
        );
      }
      if (result.needsQImport) {
        addImportRequirement(imports, '@quickback/compiler', 'q', table.context.id);
      }
    }

    if (entries.length === 0) continue;
    entries.sort((a, b) => a.order - b.order);
    if (
      table.context.style === 'raw-drizzle' &&
      needsManagedMarker &&
      !options.source
        .slice(table.columns.getStart(sourceFile), table.columns.getEnd())
        .includes('quickback:audit')
    ) {
      const firstManagedIndex = entries.findIndex((entry) => entry.order >= 20);
      entries.splice(firstManagedIndex < 0 ? entries.length : firstManagedIndex, 0, {
        source: MANAGED_MARKER,
        order: 19,
      });
    }
    edits.push(
      ...planObjectInsertion(
        sourceFile,
        table.columns,
        entries.map((entry) => entry.source),
        table.context.id,
      ),
    );
  }

  for (const [moduleName, requirement] of imports) {
    planNamedImport(
      ts,
      sourceFile,
      moduleName,
      requirement,
      options.fileName,
      edits,
      diagnostics,
    );
  }

  let updatedSource = options.source;
  try {
    updatedSource = applySchemaSourceEdits(options.source, edits);
  } catch (error) {
    diagnostics.push({
      code: 'OVERLAPPING_SOURCE_EDITS',
      fileName: options.fileName,
      message: error instanceof Error ? error.message : String(error),
    });
  }

  return {
    fileName: options.fileName,
    source: options.source,
    tables: discovered.map((table) => table.context),
    edits,
    diagnostics,
    updatedSource,
    changed: updatedSource !== options.source,
    canApply: diagnostics.length === 0,
  };
}

function isManagedGroupRequirement(
  requirement: ManagedColumnRequirement,
): requirement is ManagedGroupRequirement {
  return requirement.kind === 'audit' || requirement.kind === 'soft-delete';
}

export function applySchemaSourceEdits(
  source: string,
  edits: ReadonlyArray<SchemaSourceEdit>,
): string {
  const ordered = [...edits].sort((left, right) => {
    if (left.start !== right.start) return right.start - left.start;
    return right.end - left.end;
  });
  let previousStart = source.length + 1;
  let output = source;
  for (const edit of ordered) {
    if (
      edit.start < 0 ||
      edit.end < edit.start ||
      edit.end > source.length ||
      edit.end > previousStart
    ) {
      throw new Error(
        `Schema source edits overlap or address an invalid span: ${edit.start}..${edit.end} (${edit.reason}).`,
      );
    }
    output = output.slice(0, edit.start) + edit.text + output.slice(edit.end);
    previousStart = edit.start;
  }
  return output;
}

function discoverTables(
  ts: TypeScriptApi,
  sourceFile: TypeScript.SourceFile,
  defaultDialect: SchemaDialect,
  namingConvention: NamingConvention,
): DiscoveredTable[] {
  const resourceByVariable = new Map<string, TypeScript.Expression>();

  const collectResources = (node: TypeScript.Node): void => {
    if (ts.isCallExpression(node) && isNamedCall(ts, node.expression, 'defineTable')) {
      const tableArg = node.arguments[0];
      const configArg = node.arguments[1];
      if (tableArg && ts.isIdentifier(tableArg) && configArg) {
        resourceByVariable.set(tableArg.text, configArg);
      }
    }
    ts.forEachChild(node, collectResources);
  };
  collectResources(sourceFile);

  const tables: DiscoveredTable[] = [];
  const occurrenceByName = new Map<string, number>();
  const visit = (node: TypeScript.Node): void => {
    if (!ts.isCallExpression(node)) {
      ts.forEachChild(node, visit);
      return;
    }

    const callKind = tableCallKind(ts, node.expression);
    if (!callKind) {
      ts.forEachChild(node, visit);
      return;
    }
    const nameArg = node.arguments[0];
    const bodyArg = node.arguments[1];
    if (!nameArg || !ts.isStringLiteralLike(nameArg) || !bodyArg) {
      ts.forEachChild(node, visit);
      return;
    }

    const variableName = enclosingVariableName(ts, node);
    let columns: TypeScript.ObjectLiteralExpression | undefined;
    let resource: TypeScript.Expression | undefined;
    let style: SchemaTableStyle;
    let dialect = defaultDialect;

    if (callKind === 'feature') {
      if (!ts.isObjectLiteralExpression(bodyArg)) {
        ts.forEachChild(node, visit);
        return;
      }
      columns = objectPropertyObject(ts, bodyArg, 'columns');
      resource = bodyArg;
      style = 'q-feature';
    } else if (callKind === 'q-table') {
      if (!ts.isObjectLiteralExpression(bodyArg)) {
        ts.forEachChild(node, visit);
        return;
      }
      columns = bodyArg;
      resource = variableName ? resourceByVariable.get(variableName) : undefined;
      style = 'q-table';
    } else {
      if (!ts.isObjectLiteralExpression(bodyArg)) {
        ts.forEachChild(node, visit);
        return;
      }
      columns = bodyArg;
      resource = variableName ? resourceByVariable.get(variableName) : undefined;
      style = 'raw-drizzle';
      dialect = callKind === 'pg-table' ? 'postgres' : 'sqlite';
    }

    if (!columns) {
      ts.forEachChild(node, visit);
      return;
    }
    const tableName = nameArg.text;
    const occurrence = (occurrenceByName.get(tableName) ?? 0) + 1;
    occurrenceByName.set(tableName, occurrence);
    tables.push({
      context: {
        id: `${tableName}@${occurrence}`,
        fileName: sourceFile.fileName,
        tableName,
        variableName,
        style,
        dialect,
        namingConvention,
        hasResourceConfig: resource !== undefined,
        deleteMode: resource ? readDeleteMode(ts, resource) : 'default',
        batchDeleteMode: resource ? readBatchDeleteMode(ts, resource) : 'default',
        sourceStart: node.getStart(sourceFile),
        sourceEnd: node.getEnd(),
      },
      columns,
    });
    // Table calls cannot contain another table definition in their columns.
  };
  visit(sourceFile);
  return tables.sort((left, right) => left.context.sourceStart - right.context.sourceStart);
}

type TableCallKind = 'feature' | 'q-table' | 'pg-table' | 'sqlite-table';

function tableCallKind(
  ts: TypeScriptApi,
  expression: TypeScript.LeftHandSideExpression,
): TableCallKind | undefined {
  if (ts.isIdentifier(expression)) {
    if (expression.text === 'feature') return 'feature';
    if (expression.text === 'pgTable') return 'pg-table';
    if (expression.text === 'sqliteTable') return 'sqlite-table';
    return undefined;
  }
  if (!ts.isPropertyAccessExpression(expression)) return undefined;
  if (expression.name.text === 'pgTable') return 'pg-table';
  if (expression.name.text === 'sqliteTable') return 'sqlite-table';
  if (
    ts.isIdentifier(expression.expression) &&
    (expression.expression.text === 'q' || expression.expression.text === 'qb')
  ) {
    if (expression.name.text === 'table') return 'q-table';
    if (expression.name.text === 'feature') return 'feature';
  }
  return undefined;
}

function enclosingVariableName(
  ts: TypeScriptApi,
  call: TypeScript.CallExpression,
): string | undefined {
  const parent = call.parent;
  return ts.isVariableDeclaration(parent) && ts.isIdentifier(parent.name)
    ? parent.name.text
    : undefined;
}

function isNamedCall(
  ts: TypeScriptApi,
  expression: TypeScript.LeftHandSideExpression,
  name: string,
): boolean {
  return (
    (ts.isIdentifier(expression) && expression.text === name) ||
    (ts.isPropertyAccessExpression(expression) && expression.name.text === name)
  );
}

function objectPropertyObject(
  ts: TypeScriptApi,
  object: TypeScript.ObjectLiteralExpression,
  name: string,
): TypeScript.ObjectLiteralExpression | undefined {
  const property = object.properties.find(
    (candidate): candidate is TypeScript.PropertyAssignment =>
      ts.isPropertyAssignment(candidate) && propertyName(ts, candidate.name) === name,
  );
  return property && ts.isObjectLiteralExpression(property.initializer)
    ? property.initializer
    : undefined;
}

function readDeleteMode(
  ts: TypeScriptApi,
  expression: TypeScript.Expression,
): StaticDeleteMode {
  if (!ts.isObjectLiteralExpression(expression)) return 'dynamic';
  let deleteExpression = propertyInitializer(ts, expression, 'delete');
  if (!deleteExpression) {
    const crud = propertyInitializer(ts, expression, 'crud');
    if (crud && !ts.isObjectLiteralExpression(crud)) return 'dynamic';
    if (crud && ts.isObjectLiteralExpression(crud)) {
      deleteExpression = propertyInitializer(ts, crud, 'delete');
    }
  }
  if (!deleteExpression) return 'default';
  if (!ts.isObjectLiteralExpression(deleteExpression)) return 'dynamic';
  const mode = propertyInitializer(ts, deleteExpression, 'mode');
  if (!mode) return 'default';
  if (!ts.isStringLiteralLike(mode)) return 'dynamic';
  if (mode.text === 'hard') return 'hard';
  if (mode.text === 'soft') return 'soft';
  return 'dynamic';
}

/**
 * `batchDelete.mode`, resolved with the inheritance the compiler applies in
 * `resolveDeleteModes`: an absent or `false` batchDelete inherits the delete
 * mode, and an explicit batchDelete without its own `mode` inherits it too.
 *
 * This exists because soft-delete columns are required unless EVERY delete
 * path is hard. Reading `delete.mode` alone silently under-reports the
 * requirement for `delete: { mode: 'hard' }, batchDelete: { mode: 'soft' }`,
 * which the compiler then rejects.
 */
function readBatchDeleteMode(
  ts: TypeScriptApi,
  expression: TypeScript.Expression,
): StaticDeleteMode {
  if (!ts.isObjectLiteralExpression(expression)) return 'dynamic';
  const inherited = readDeleteMode(ts, expression);

  let batchExpression = propertyInitializer(ts, expression, 'batchDelete');
  if (!batchExpression) {
    const crud = propertyInitializer(ts, expression, 'crud');
    if (crud && !ts.isObjectLiteralExpression(crud)) return 'dynamic';
    if (crud && ts.isObjectLiteralExpression(crud)) {
      batchExpression = propertyInitializer(ts, crud, 'batchDelete');
    }
  }
  // Absent, or disabled outright — the delete mode governs.
  if (!batchExpression) return inherited;
  if (batchExpression.kind === ts.SyntaxKind.FalseKeyword) return inherited;
  if (!ts.isObjectLiteralExpression(batchExpression)) return 'dynamic';

  const mode = propertyInitializer(ts, batchExpression, 'mode');
  if (!mode) return inherited;
  if (!ts.isStringLiteralLike(mode)) return 'dynamic';
  if (mode.text === 'hard') return 'hard';
  if (mode.text === 'soft') return 'soft';
  return 'dynamic';
}

function propertyInitializer(
  ts: TypeScriptApi,
  object: TypeScript.ObjectLiteralExpression,
  name: string,
): TypeScript.Expression | undefined {
  for (const property of object.properties) {
    if (ts.isPropertyAssignment(property) && propertyName(ts, property.name) === name) {
      return property.initializer;
    }
    if (ts.isShorthandPropertyAssignment(property) && property.name.text === name) {
      return property.name;
    }
  }
  return undefined;
}

function propertyName(
  ts: TypeScriptApi,
  name: TypeScript.PropertyName,
): string | undefined {
  if (ts.isIdentifier(name) || ts.isPrivateIdentifier(name)) return name.text;
  if (ts.isStringLiteralLike(name) || ts.isNumericLiteral(name)) return name.text;
  return undefined;
}

function planManagedGroup(
  ts: TypeScriptApi,
  sourceFile: TypeScript.SourceFile,
  table: DiscoveredTable,
  requirement: ManagedGroupRequirement,
  fileName: string,
  diagnostics: SchemaSyncDiagnostic[],
): {
  entries: PlannedEntry[];
  requiredImports: Set<string>;
  needsQImport: boolean;
  needsMarker: boolean;
} {
  if (table.context.style !== 'raw-drizzle') {
    const helperSpread = findQHelperSpread(ts, table.columns, requirement.qHelper);
    const explicit = table.columns.properties.filter((property) => {
      if (!ts.isPropertyAssignment(property)) return false;
      const name = propertyName(ts, property.name);
      return requirement.columns.some((column) => column.jsName === name);
    });
    if (helperSpread && explicit.length > 0) {
      diagnostics.push({
        code: 'DUPLICATE_MANAGED_REPRESENTATION',
        fileName,
        tableId: table.context.id,
        tableName: table.context.tableName,
        start: explicit[0]!.getStart(sourceFile),
        end: explicit[explicit.length - 1]!.getEnd(),
        expected: `...q.${requirement.qHelper}(),`,
        actual: explicit.map((property) => property.getText(sourceFile)).join('\n'),
        message:
          `Table "${table.context.tableName}" declares both ...q.${requirement.qHelper}() ` +
          `and explicit ${requirement.kind} columns. Remove the explicit duplicates manually.`,
      });
      return {
        entries: [],
        requiredImports: new Set(),
        needsQImport: false,
        needsMarker: false,
      };
    }
    if (helperSpread) {
      return {
        entries: [],
        requiredImports: new Set(),
        needsQImport: false,
        needsMarker: false,
      };
    }
    if (explicit.length > 0) {
      diagnostics.push({
        code: 'DIVERGENT_MANAGED_COLUMN',
        fileName,
        tableId: table.context.id,
        tableName: table.context.tableName,
        start: explicit[0]!.getStart(sourceFile),
        end: explicit[explicit.length - 1]!.getEnd(),
        expected: `...q.${requirement.qHelper}(),`,
        actual: explicit.map((property) => property.getText(sourceFile)).join('\n'),
        message:
          `Table "${table.context.tableName}" uses explicit q-DSL ${requirement.kind} ` +
          `columns. Quickback cannot prove that hand-written q chains match the managed ` +
          `contract; replace them manually with ...q.${requirement.qHelper}().`,
      });
      return {
        entries: [],
        requiredImports: new Set(),
        needsQImport: false,
        needsMarker: false,
      };
    }
    return {
      entries: [{ source: `...q.${requirement.qHelper}(),`, order: groupOrder(requirement.kind) }],
      requiredImports: new Set(),
      needsQImport: true,
      needsMarker: false,
    };
  }

  const entries: PlannedEntry[] = [];
  const requiredImports = new Set<string>();
  for (const column of requirement.columns) {
    const sqlName = managedSqlName(column, table.context.namingConvention);
    const expectedExpression = managedColumnExpr(column, table.context.dialect, sqlName);
    const expectedLine = managedColumnLine(
      column,
      table.context.dialect,
      table.context.namingConvention,
    );
    const matches = matchingRawProperties(ts, table.columns, column.jsName, sqlName);
    if (matches.length > 1) {
      diagnostics.push(duplicateColumnDiagnostic(fileName, sourceFile, table, column.jsName, matches));
      continue;
    }
    const existing = matches[0];
    if (!existing) {
      entries.push({ source: expectedLine, order: groupOrder(requirement.kind) });
      for (const identifier of calledBuilderIdentifiers(expectedExpression)) {
        requiredImports.add(identifier);
      }
      continue;
    }
    if (
      propertyName(ts, existing.name) !== column.jsName ||
      !expressionMatches(sourceFile, existing.initializer, [expectedExpression])
    ) {
      diagnostics.push(
        divergentColumnDiagnostic(
          fileName,
          sourceFile,
          table,
          existing,
          expectedLine,
          column.jsName,
        ),
      );
    }
  }
  return {
    entries,
    requiredImports,
    needsQImport: false,
    needsMarker: entries.length > 0,
  };
}

function planPhysicalColumn(
  ts: TypeScriptApi,
  sourceFile: TypeScript.SourceFile,
  table: DiscoveredTable,
  requirement: ManagedPhysicalColumnRequirement,
  fileName: string,
  diagnostics: SchemaSyncDiagnostic[],
): {
  entries: PlannedEntry[];
  requiredImports: Set<string>;
  needsQImport: boolean;
} {
  const raw = table.context.style === 'raw-drizzle';
  const matches = raw
    ? matchingRawProperties(ts, table.columns, requirement.jsName, requirement.sqlName)
    : matchingQProperties(ts, table.columns, requirement.jsName);
  if (matches.length > 1) {
    diagnostics.push(
      duplicateColumnDiagnostic(fileName, sourceFile, table, requirement.jsName, matches),
    );
    return { entries: [], requiredImports: new Set(), needsQImport: false };
  }

  const expression = raw ? requirement.rawExpression : requirement.qExpression;
  const accepted = raw
    ? [requirement.rawExpression, ...(requirement.acceptedRawExpressions ?? [])]
    : [requirement.qExpression, ...(requirement.acceptedQExpressions ?? [])];
  const expected = `${requirement.jsName}: ${expression},`;
  const existing = matches[0];
  if (existing) {
    if (
      propertyName(ts, existing.name) !== requirement.jsName ||
      !expressionMatches(sourceFile, existing.initializer, accepted)
    ) {
      diagnostics.push(
        divergentColumnDiagnostic(
          fileName,
          sourceFile,
          table,
          existing,
          expected,
          requirement.jsName,
        ),
      );
    }
    return { entries: [], requiredImports: new Set(), needsQImport: false };
  }

  return {
    entries: [{ source: expected, order: requirement.kind === 'id' ? 0 : 10 }],
    requiredImports: raw
      ? new Set(calledBuilderIdentifiers(requirement.rawExpression))
      : new Set(),
    needsQImport: !raw,
  };
}

function groupOrder(kind: 'audit' | 'soft-delete'): number {
  return kind === 'audit' ? 20 : 30;
}

function findQHelperSpread(
  ts: TypeScriptApi,
  object: TypeScript.ObjectLiteralExpression,
  helper: string,
): TypeScript.SpreadAssignment | undefined {
  return object.properties.find((property): property is TypeScript.SpreadAssignment => {
    if (!ts.isSpreadAssignment(property) || !ts.isCallExpression(property.expression)) {
      return false;
    }
    const callee = property.expression.expression;
    return (
      ts.isPropertyAccessExpression(callee) &&
      ts.isIdentifier(callee.expression) &&
      callee.expression.text === 'q' &&
      callee.name.text === helper &&
      property.expression.arguments.length === 0
    );
  });
}

function matchingQProperties(
  ts: TypeScriptApi,
  object: TypeScript.ObjectLiteralExpression,
  jsName: string,
): TypeScript.PropertyAssignment[] {
  return object.properties.filter(
    (property): property is TypeScript.PropertyAssignment =>
      ts.isPropertyAssignment(property) && propertyName(ts, property.name) === jsName,
  );
}

function matchingRawProperties(
  ts: TypeScriptApi,
  object: TypeScript.ObjectLiteralExpression,
  jsName: string,
  sqlName: string,
): TypeScript.PropertyAssignment[] {
  return object.properties.filter((property): property is TypeScript.PropertyAssignment => {
    if (!ts.isPropertyAssignment(property)) return false;
    if (propertyName(ts, property.name) === jsName) return true;
    return firstStringArgument(ts, property.initializer) === sqlName;
  });
}

function firstStringArgument(
  ts: TypeScriptApi,
  expression: TypeScript.Expression,
): string | undefined {
  let current = expression;
  while (ts.isCallExpression(current)) {
    const first = current.arguments[0];
    if (first && ts.isStringLiteralLike(first)) return first.text;
    const callee = current.expression;
    if (!ts.isPropertyAccessExpression(callee)) break;
    current = callee.expression;
  }
  return undefined;
}

function expressionMatches(
  sourceFile: TypeScript.SourceFile,
  expression: TypeScript.Expression,
  accepted: ReadonlyArray<string>,
): boolean {
  const actual = normalizeExpression(expression.getText(sourceFile));
  return accepted.some((candidate) => normalizeExpression(candidate) === actual);
}

function normalizeExpression(source: string): string {
  return source
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\/\/[^\r\n]*/g, '')
    .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_match, value: string) => `"${value}"`)
    .replace(/\s+/g, '');
}

function calledBuilderIdentifiers(expression: string): string[] {
  const identifiers = new Set<string>();
  for (const match of expression.matchAll(/(?:^|[\s:,{(])([A-Za-z_$][\w$]*)\s*\(/g)) {
    const identifier = match[1]!;
    if (identifier !== 'Date') identifiers.add(identifier);
  }
  return [...identifiers].sort();
}

function duplicateColumnDiagnostic(
  fileName: string,
  sourceFile: TypeScript.SourceFile,
  table: DiscoveredTable,
  jsName: string,
  properties: TypeScript.PropertyAssignment[],
): SchemaSyncDiagnostic {
  return {
    code: 'DUPLICATE_TABLE_COLUMN',
    fileName,
    tableId: table.context.id,
    tableName: table.context.tableName,
    start: properties[0]!.getStart(sourceFile),
    end: properties[properties.length - 1]!.getEnd(),
    actual: properties.map((property) => property.getText(sourceFile)).join('\n'),
    message:
      `Table "${table.context.tableName}" declares managed column "${jsName}" more than once. ` +
      `Resolve the duplicate manually before syncing.`,
  };
}

function divergentColumnDiagnostic(
  fileName: string,
  sourceFile: TypeScript.SourceFile,
  table: DiscoveredTable,
  property: TypeScript.PropertyAssignment,
  expected: string,
  jsName: string,
): SchemaSyncDiagnostic {
  return {
    code: 'DIVERGENT_MANAGED_COLUMN',
    fileName,
    tableId: table.context.id,
    tableName: table.context.tableName,
    start: property.getStart(sourceFile),
    end: property.getEnd(),
    expected,
    actual: property.getText(sourceFile),
    message:
      `Table "${table.context.tableName}" has a divergent managed column "${jsName}". ` +
      `Quickback will not rewrite it automatically; replace it with the canonical form.`,
  };
}

function planObjectInsertion(
  sourceFile: TypeScript.SourceFile,
  object: TypeScript.ObjectLiteralExpression,
  entries: string[],
  tableId: string,
): SchemaSourceEdit[] {
  if (entries.length === 0) return [];
  const source = sourceFile.text;
  const lineEnding = source.includes('\r\n') ? '\r\n' : '\n';
  const open = object.getStart(sourceFile);
  const close = object.getEnd() - 1;
  const multiline = source.slice(open, close).includes('\n');
  const edits: SchemaSourceEdit[] = [];
  const last = object.properties[object.properties.length - 1];
  const hasTrailingComma = last
    ? source.slice(last.getEnd(), close).includes(',')
    : false;

  if (!multiline) {
    if (last) {
      const tail = source.slice(last.getEnd(), close);
      const preservesComment = tail.includes('//') || tail.includes('/*');
      edits.push({
        start: last.getEnd(),
        end: close,
        text: preservesComment
          ? `${hasTrailingComma ? '' : ','}${tail}${/\s$/.test(tail) ? '' : ' '}${entries.join(' ')} `
          : `, ${entries.join(' ')} `,
        tableId,
        reason: 'insert managed columns into single-line table object',
      });
    } else {
      edits.push({
        start: open + 1,
        end: close,
        text: ` ${entries.join(' ')} `,
        tableId,
        reason: 'insert managed columns into empty single-line table object',
      });
    }
    return edits;
  }

  if (last && !hasTrailingComma) {
    edits.push({
      start: last.getEnd(),
      end: last.getEnd(),
      text: ',',
      tableId,
      reason: 'terminate the previous table column before managed-column insertion',
    });
  }

  const closeLineStart = source.lastIndexOf('\n', close - 1) + 1;
  const closeIndent = source.slice(closeLineStart, close).match(/^[ \t]*/)?.[0] ?? '';
  const first = object.properties[0];
  let entryIndent: string;
  if (first) {
    const firstStart = first.getStart(sourceFile);
    const firstLineStart = source.lastIndexOf('\n', firstStart - 1) + 1;
    entryIndent = source.slice(firstLineStart, firstStart);
  } else {
    entryIndent = closeIndent.includes('\t') ? `${closeIndent}\t` : `${closeIndent}  `;
  }
  const text = entries.map((entry) => `${entryIndent}${entry}`).join(lineEnding) + lineEnding;
  edits.push({
    start: closeLineStart,
    end: closeLineStart,
    text,
    tableId,
    reason: 'insert canonical managed-column declarations',
  });
  return edits;
}

function addImportRequirement(
  imports: Map<string, ImportRequirement>,
  moduleName: string,
  identifier: string,
  tableId: string,
): void {
  let requirement = imports.get(moduleName);
  if (!requirement) {
    requirement = { identifiers: new Set(), tableIds: new Set() };
    imports.set(moduleName, requirement);
  }
  requirement.identifiers.add(identifier);
  requirement.tableIds.add(tableId);
}

function drizzleCoreModule(dialect: SchemaDialect): string {
  return dialect === 'postgres' ? 'drizzle-orm/pg-core' : 'drizzle-orm/sqlite-core';
}

function planNamedImport(
  ts: TypeScriptApi,
  sourceFile: TypeScript.SourceFile,
  moduleName: string,
  requirement: ImportRequirement,
  fileName: string,
  edits: SchemaSourceEdit[],
  diagnostics: SchemaSyncDiagnostic[],
): void {
  const candidates = sourceFile.statements.filter(
    (statement): statement is TypeScript.ImportDeclaration =>
      ts.isImportDeclaration(statement) &&
      ts.isStringLiteralLike(statement.moduleSpecifier) &&
      statement.moduleSpecifier.text === moduleName &&
      !!statement.importClause &&
      !statement.importClause.isTypeOnly &&
      !!statement.importClause.namedBindings &&
      ts.isNamedImports(statement.importClause.namedBindings),
  );
  const present = new Set<string>();
  for (const candidate of candidates) {
    const bindings = candidate.importClause!.namedBindings as TypeScript.NamedImports;
    for (const element of bindings.elements) present.add(element.name.text);
  }
  const missing = [...requirement.identifiers].filter((name) => !present.has(name)).sort();
  if (missing.length === 0) return;
  const target = candidates[0];
  if (!target) {
    diagnostics.push({
      code: 'MISSING_VALUE_IMPORT',
      fileName,
      tableId: requirement.tableIds.size === 1 ? [...requirement.tableIds][0] : undefined,
      expected: `import { ${missing.join(', ')} } from "${moduleName}";`,
      message:
        `Managed-column synchronization needs { ${missing.join(', ')} } from ` +
        `"${moduleName}", but no plain named value import can be widened. ` +
        `Add the import manually; namespace and type-only imports are not rewritten.`,
    });
    return;
  }

  const bindings = target.importClause!.namedBindings as TypeScript.NamedImports;
  const source = sourceFile.text;
  const close = bindings.getEnd() - 1;
  const multiline = source.slice(bindings.getStart(sourceFile), close).includes('\n');
  const last = bindings.elements[bindings.elements.length - 1];
  if (!multiline) {
    if (last) {
      const tail = source.slice(last.getEnd(), close);
      const preservesComment = tail.includes('//') || tail.includes('/*');
      edits.push({
        start: last.getEnd(),
        end: close,
        text: preservesComment
          ? `${tail.includes(',') ? '' : ','}${tail}${/\s$/.test(tail) ? '' : ' '}${missing.join(', ')} `
          : `, ${missing.join(', ')} `,
        reason: `add required value imports from ${moduleName}`,
      });
    } else {
      edits.push({
        start: bindings.getStart(sourceFile) + 1,
        end: close,
        text: ` ${missing.join(', ')} `,
        reason: `add required value imports from ${moduleName}`,
      });
    }
    return;
  }
  if (last && !source.slice(last.getEnd(), close).includes(',')) {
    edits.push({
      start: last.getEnd(),
      end: last.getEnd(),
      text: ',',
      reason: `terminate the existing ${moduleName} import before widening it`,
    });
  }
  const lineEnding = source.includes('\r\n') ? '\r\n' : '\n';
  const closeLineStart = source.lastIndexOf('\n', close - 1) + 1;
  const first = bindings.elements[0];
  const indent = first
    ? source.slice(source.lastIndexOf('\n', first.getStart(sourceFile) - 1) + 1, first.getStart(sourceFile))
    : '  ';
  edits.push({
    start: closeLineStart,
    end: closeLineStart,
    text: missing.map((name) => `${indent}${name},`).join(lineEnding) + lineEnding,
    reason: `add required value imports from ${moduleName}`,
  });
}
